/**
 * Number Pill
 *
 * Shared visual treatment for numbers called out in narrative text
 * (`**74**` renders as a monospace value on a translucent white "pill").
 *
 * The pill has to sit inside the line box of whatever text style it lands in —
 * body, caption, headline, headline_large, a markdown header — because a
 * painted box taller than the leading collides with the pills on the next
 * wrapped line. Two things keep that from happening:
 *
 * 1. Every dimension is em-based, so the treatment scales with the text
 *    instead of being a fixed 8px that looks heavy on a caption and thin on a
 *    display headline.
 * 2. Values that stay on one line are painted as an inline-block with its own
 *    tight line-height. The painted height of an *inline* box is the font's
 *    content area (~1.2em for our faces) no matter how little padding it
 *    carries — taller than headline_large's leading all by itself. An
 *    inline-block's height is line-height + padding instead, which we control:
 *    PILL_HEIGHT_EM (1.04em) clears every leading we ship.
 *
 * A value that can wrap and is too wide to hold on one line (a whole bolded
 * sentence that happens to start with a digit) keeps flowing inline and gets a
 * pill per line fragment with no vertical padding — the tightest an inline box
 * can be.
 *
 * That leaves the two paths at slightly different heights (1.04em vs the
 * font's ~1.2em content area), which shows when a wrapping phrase and a figure
 * land on the same line. It is a deliberate call: matching them would mean
 * either splitting long phrases into per-word pills, dropping the pill from
 * them, or loosening every leading to fit the taller box. Padding cannot close
 * the gap — an inline box has no room below its content area.
 */

import type { CSSProperties } from "react";

/** Internal leading of a short pill. Digits self-centre in the tight box. */
const PILL_LEADING = 0.72;

/** Space above/below the pill's line box, in em of the surrounding text. */
const PILL_PAD_Y_EM = 0.16;

/**
 * Horizontal breathing room around the value, in em of the surrounding text.
 * The absolute cap only bites above a 5rem text size — past every preset, but
 * reachable through a per-item `textSize` override — where a proportional pad
 * would put a slab of background either side of a two-digit number.
 */
const PILL_PAD_X = "min(0.2em, 1rem)";

/**
 * Painted height of a pill held on one line, in em of the surrounding text
 * (1.04em). Every text style's line-height has to exceed this or its pills
 * collide when the text wraps; see --text-scale-leading-* in the app's
 * index.css, where headline_large is set from this number.
 */
export const PILL_HEIGHT_EM = PILL_LEADING + 2 * PILL_PAD_Y_EM;

/**
 * Characters a browser may break a line at. A value containing none of them
 * cannot wrap however we style it, so holding it on one line costs nothing;
 * one that contains them has to earn `nowrap` by being narrow enough.
 */
const BREAKABLE_RE = /[-/\s\u00AD\u2010-\u2015]/;

/**
 * How wide a breakable value may be and still be held on one line, in
 * characters. The narrowest column we render into is `max-w-sm` less `px-8`
 * (~6.8em at the 3rem headline_large scale on a 390px phone); at ~0.6em per
 * monospace character, 10 characters plus padding is ~6.4em and fits, while a
 * wider pill would be clipped by the group's `overflow-x-hidden`. Still covers
 * the short units authors pair with a figure ("14 weeks", "$6 million").
 */
const NOWRAP_MAX_CHARS = 10;

/** Classes shared by every pill (colour + face come from Tailwind). */
export const NUMBER_PILL_CLASS = "font-mono bg-white/20";

/** Whether this value keeps its pill — and itself — on a single line. */
function fitsOnOneLine(content: string): boolean {
  return !BREAKABLE_RE.test(content) || content.length <= NOWRAP_MAX_CHARS;
}

/**
 * Inline styles for a number pill wrapping `content`.
 *
 * Values that stay on one line get the fixed-height inline-block. A breakable
 * value too wide for that keeps flowing inline and gets a pill per line
 * fragment instead.
 */
export function numberPillStyle(content: string): CSSProperties {
  const shared: CSSProperties = {
    borderRadius: "0.2em",
    fontWeight: 600,
  };

  if (!fitsOnOneLine(content)) {
    return {
      ...shared,
      // No vertical padding: an inline box already paints the font's whole
      // content area, and anything more eats into the neighbouring line.
      padding: `0 ${PILL_PAD_X}`,
      boxDecorationBreak: "clone",
      WebkitBoxDecorationBreak: "clone",
    };
  }

  return {
    ...shared,
    display: "inline-block",
    lineHeight: PILL_LEADING,
    padding: `${PILL_PAD_Y_EM}em ${PILL_PAD_X}`,
    whiteSpace: "nowrap",
  };
}
